import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { useHistory, useLocation } from 'react-router-dom';
import { getAuthData } from 'utils/authData';

const LOOPBACK = /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/;

/**
 * A SongFund micro-app (marketing plan builder, tour budget) embedded as an iframe.
 *
 * The host hash is the iframe hash: `src` changes by fragment only, which the browser treats as
 * a fragment navigation (no reload), and the app reports its own hash changes back
 * (`songfund:hash`) so the address bar always points at what is open.
 *
 * `auth` (dev only): when the app runs on another loopback origin (Vite dev server) its
 * localStorage has no session, so the `Authorization` header is handed over by postMessage.
 * Same-origin builds never use this.
 */
const EmbeddedApp: React.FC<{ title: string; url: string; auth?: boolean; allow?: string }> = ({
  title,
  url,
  auth = false,
  allow = 'clipboard-write',
}) => {
  const href = new URL(url, window.location.origin).href;
  const origin = new URL(href).origin;
  const bridgeAuth = auth && origin !== window.location.origin && LOOPBACK.test(origin);

  const frame = useRef<HTMLIFrameElement>(null);
  const history = useHistory();
  const { hash } = useLocation();

  const sendAuth = () => {
    const authorization = getAuthData().Authorization;
    if (!bridgeAuth || !authorization || !frame.current?.contentWindow) return;
    frame.current.contentWindow.postMessage({ type: 'songfund:auth', authorization }, origin);
  };

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== origin) return;
      if (event.data?.type === 'songfund:auth-request') sendAuth();
      if (event.data?.type === 'songfund:hash')
        history.replace({ ...history.location, hash: event.data.hash });
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [history, origin]);

  return (
    <Box width="100%" display="flex" flex={1} minHeight={0}>
      <iframe
        ref={frame}
        title={title}
        src={href + (hash || '#')}
        onLoad={sendAuth}
        allow={allow}
        allowFullScreen
        style={{ width: '100%', height: '100%', border: 'none', flex: 1 }}
      />
    </Box>
  );
};

export default EmbeddedApp;
